import { useState, type FormEvent } from 'react';
import { Link } from '@tanstack/react-router';
import {
  KeyRound,
  Plus,
  Trash2,
  Copy,
  Check,
  AlertCircle,
  Power,
  Loader2,
  BookOpen,
  ShieldAlert,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useApiKeys, useCreateApiKey, useDeleteApiKey, useToggleApiKey } from '@/hooks/useApiKeys';
import { useConfirm } from '@/components/ConfirmDialog';
import { useMe } from '@/hooks/useAuth';
import { ApiError } from '@/lib/api-client';
import { formatDate, cn } from '@/lib/utils';

export function ApiKeysPage() {
  const { data, isPending } = useApiKeys();
  const create = useCreateApiKey();
  const toggle = useToggleApiKey();
  const del = useDeleteApiKey();
  const confirm = useConfirm();
  const me = useMe();
  const activeOrg = me.data?.organizations.find((o) => o.id === me.data?.active_org_id);
  const isAdmin = activeOrg?.role === 'admin';

  const [name, setName] = useState('');
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const res = await create.mutateAsync(name);
      setNewKey(res.raw_key);
      setName('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Oluşturulamadı');
    }
  };

  const onCopy = async () => {
    if (!newKey) return;
    await navigator.clipboard.writeText(newKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <KeyRound className="w-5 h-5" /> API Anahtarları
        </h1>
        <p className="text-muted-foreground text-sm">
          Programatik erişim için API key oluştur. n8n / Zapier / custom script'lerle entegre et.{' '}
          <Link to="/api-docs" className="text-primary hover:underline inline-flex items-center gap-1">
            <BookOpen className="w-3.5 h-3.5" /> API Dokümanı
          </Link>
        </p>
      </div>

      {!isAdmin && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 text-sm px-3 py-2 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 text-amber-600 dark:text-amber-400" />
          Sadece organizasyon yöneticileri API key oluşturabilir.
        </div>
      )}

      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Yeni API Key</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onCreate} className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="n">İsim (tanımlayıcı)</Label>
                <Input
                  id="n"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Örn: n8n production, Zapier, My script"
                  required
                />
              </div>
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/30 px-3 py-2">
                  {error}
                </div>
              )}
              <Button type="submit" disabled={create.isPending || !name.trim()}>
                {create.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Oluştur
              </Button>
            </form>

            {newKey && (
              <div className="mt-4 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 space-y-2">
                <div className="flex items-start gap-2">
                  <ShieldAlert className="w-4 h-4 text-emerald-700 dark:text-emerald-400 shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-semibold text-emerald-800 dark:text-emerald-200">
                      Key oluşturuldu — şimdi kopyala!
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Bu anahtarı bir daha göremezsin. Güvenli bir yere kaydet.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <code className="flex-1 min-w-0 font-mono text-xs bg-background px-2 py-2 rounded break-all">
                    {newKey}
                  </code>
                  <Button size="sm" variant="outline" onClick={onCopy}>
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setNewKey(null)}>
                  Kapat
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Mevcut Keyler</CardTitle>
          <CardDescription>Rate limit: dakikada 60 istek / key.</CardDescription>
        </CardHeader>
        <CardContent>
          {isPending ? (
            <div className="text-muted-foreground">Yükleniyor…</div>
          ) : data?.keys.length === 0 ? (
            <p className="text-sm text-muted-foreground">Henüz API key yok.</p>
          ) : (
            <div className="space-y-2">
              {data?.keys.map((k) => (
                <div
                  key={k.id}
                  className={cn('rounded-md border p-3 flex items-center gap-3', !k.enabled && 'opacity-60')}
                >
                  <KeyRound className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{k.name ?? '(isimsiz)'}</div>
                    <div className="text-xs text-muted-foreground space-x-2">
                      <code className="font-mono">{k.prefix}…</code>
                      <span>· Oluşturuldu: {formatDate(k.created_at)}</span>
                      {k.last_used_at && <span>· Son kullanım: {formatDate(k.last_used_at)}</span>}
                    </div>
                  </div>
                  {isAdmin && (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => toggle.mutate({ id: k.id, enabled: !k.enabled })}
                        disabled={toggle.isPending}
                      >
                        <Power
                          className={cn(
                            'w-3.5 h-3.5',
                            k.enabled && 'text-emerald-600 dark:text-emerald-400',
                          )}
                        />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          const ok = await confirm({
                            title: 'Key\'i sil?',
                            description: 'Bu API key kalıcı olarak silinecek. Kullanan sistemler 401 almaya başlar.',
                            confirmText: 'Sil',
                            danger: true,
                          });
                          if (ok) del.mutate(k.id);
                        }}
                        disabled={del.isPending}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
